import React, { memo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { validators } from "investira.sdk";
import { Loading, CenterInView } from "investira.react.components";
import services from "../../../services";
import app from "../../../utils/app";
import usePageReload from "../../../hooks/usePageReload";

import {
  acUserUpdate,
  acInfoChange,
  acEntidadeChanged,
  acEntidadeCurrentMembrosChanged,
  acIndicadoresChanged,
  acCarteirasChanged,
  acMercadosChanged,
  acReportsEnquadramentoRegrasChanged,
  acOutputsEnumChanged,
  acReportsEnquadramentosChanged,
} from "../../../store/actions";

import { ErrorDataView } from "../../../components/molecules";

import withResponseHandling from "../../../hoc/withResponseHandling";

const Preload = memo((props) => {
  const dispatch = useDispatch();
  const isReloaded = usePageReload();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState(null);

  function verifyToken() {
    services.info.read(
      {},
      () => {
        preload();
      },
      () => {
        app.logout();
      }
    );
  }

  async function baseRequest(pService, pParams = {}, pIsReloaded = false) {
    return await pService(
      { ...pParams, isReloaded: pIsReloaded },
      (rRes) => {
        return rRes.data;
      },
      (rErr) => {
        throw rErr;
      }
    );
  }

  function feedRedux(pResponses) {
    try {
      const xDispatches = [
        (pData) => dispatch(acUserUpdate(pData)),
        (pData) => dispatch(acInfoChange(pData)),
        (pData) => dispatch(acEntidadeChanged(pData)),
        (pData) => dispatch(acIndicadoresChanged(pData)),
        (pData) => dispatch(acCarteirasChanged(pData)),
        (pData) => dispatch(acMercadosChanged(pData)),
        (pData) => dispatch(acReportsEnquadramentoRegrasChanged(pData)),
        (pData) => dispatch(acOutputsEnumChanged(pData)),
        (pData) => dispatch(acReportsEnquadramentosChanged(pData)),
        (pData) => dispatch(acEntidadeCurrentMembrosChanged(pData)),
      ];

      pResponses.forEach((pData, pIndex) => {
        xDispatches[pIndex](pData);
      });

      setDataLoaded(true);
    } catch (error) {
      console.error("Preload => feedRedux()", error.message);
      return error;
    }
  }

  async function preload() {
    const xRequests = [
      baseRequest(services.user.read, isReloaded),
      baseRequest(services.info.read),
      baseRequest(services.entidades.usuario.entidades),
      baseRequest(services.indicadores.list),
      baseRequest(services.carteiras.list, {
        favorito: 1,
      }),
      baseRequest(services.ativosbase.mercado),
      baseRequest(services.enquadramentosRegras.sys),
      baseRequest(services.outputs.enumaration),
      baseRequest(services.enquadramentos.list),
      baseRequest(services.entidadesUsuario.list),
    ];

    await Promise.all(xRequests)
      .then((rResponses) => {
        feedRedux(rResponses);
      })
      .catch((rErr) => {
        const xCallbacks = {
          err400: () => setError(true),
          err404: () => setError(true),
          err500: () => setError(true),
        };
        props.responseErrorHandling(rErr, xCallbacks);
      });
  }

  function handleRetry() {
    setError(null);
    verifyToken();
  }

  // Testa o token e inicia o preload
  useEffect(() => {
    props.isLoggedIn && verifyToken();
  }, [props.isLoggedIn]);

  if (!validators.isNull(error)) {
    return <ErrorDataView handleClick={handleRetry} />;
  }

  if (props.isLoggedIn === false) {
    return props.children;
  }

  if (dataLoaded) {
    return props.children;
  } else {
    return (
      <CenterInView>
        <Loading />
      </CenterInView>
    );
  }
});

Preload.propTypes = {
  children: PropTypes.element,
};

Preload.defaultProps = {};

Preload.displayName = "Preload";

export default withResponseHandling(Preload);
